import * as Sentry from "@sentry/react";
import type {DocumentPartsResponse, RenderableDocument} from "./types";

/** What a parts response was fetched for:
 *  GET /api/documents/{resourceId}/parts?segmentId=&id= */
type PartsRequest = {resourceId?: string; segmentId?: string; id?: string};

/**
 * True for an excerpt part without content. dib-app-api returns one
 * (`root: []`, `elements: {}`) when the data behind the request is broken,
 * e.g. linked law versions whose element ids no longer match (box#1795).
 * Callers show a "not found" notice in its place, never nothing, and
 * report the response with reportEmptyParts.
 */
export const isEmptyPart = (part: RenderableDocument) =>
    part.root.length === 0;

// Callers report from effects and RTK cache hits, so the same response
// object can come back many times.
const reported = new WeakSet<DocumentPartsResponse>();

/**
 * Sends a response with empty parts to Sentry, once per response object,
 * with what's needed to find the broken data. The route is on every event
 * already (request.url). Does nothing when no part is empty.
 */
export function reportEmptyParts(
    response: DocumentPartsResponse,
    {
        feature,
        request,
        pageTopicId,
    }: {
        feature: string;
        request: PartsRequest;
        /** The topic of the page the user is on, when there is one. */
        pageTopicId?: string;
    }
) {
    const emptyParts = response.parts.filter(isEmptyPart);
    if (emptyParts.length === 0 || reported.has(response)) return;
    reported.add(response);

    Sentry.captureMessage(
        "Document parts response has a part without content",
        {
            level: "error",
            tags: {feature, pageTopicId, linkId: request.id || undefined},
            // In extra, not contexts: Sentry normalizes both to a depth of
            // 3, which keeps the emptyParts entries here but turns them into
            // "[Object]" inside a named context.
            extra: {
                pageTopicId,
                request,
                emptyParts: emptyParts.map((part) => ({
                    topicId: part.topicId,
                    resourceId: part.id,
                    name: part.name,
                    segmentId: part.segmentId,
                })),
            },
        }
    );
}
